import { calculateAssembly } from "./orderAccessories";

/**
 * Проверка, является ли товар "дверью" (по наличию ширины/высоты).
 */
const isDoor = (selectedProduct) => {
  return !!selectedProduct?.width;
};

/**
 * Хелпер: посчитать "финальную" цену конкретного аксессуара, учитывая:
 *  - accessory.price
 *  - accessoryDiscount (accessory.accessoryDiscount)
 *  - accessoryAppreciation (accessory.accessoryAppreciation)
 *  - умножение на количество дверей (amount)
 */
function calcAccessoryFinalPrice(accessory, amount) {
  if (!accessory || !accessory.price) {
    return 0;
  }

  // 1) Начальная цена аксессуара
  let accPrice = accessory.price || 0;

  // 2) Умножаем на кол-во дверей (если нужно)
  accPrice *= amount;

  // 3) Удорожание аксессуара
  const accAppreciation = accessory.accessoryAppreciation || 0;
  if (accAppreciation > 0) {
    const plusVal = Math.round(accPrice * (accAppreciation / 100));
    accPrice += plusVal;
  }

  // 4) Скидка на аксессуар
  const accDiscount = accessory.accessoryDiscount || 0;
  if (accDiscount > 0) {
    const minusVal = Math.round(accPrice * (accDiscount / 100));
    accPrice -= minusVal;
  }

  return accPrice;
}

/**
 * Возвращает простую сумму всех товаров (без учёта скидок, аксессуаров и т.п.).
 */
export const getTotalPrice = (selectedProducts) => {
  if (!Array.isArray(selectedProducts) || selectedProducts.length === 0) {
    return 0;
  }

  let totalPrice = 0;
  for (let product of selectedProducts) {
    const p = product.product;
    const price = product.price || (p && p.price) || 0;
    const amount = product.amount || 1;
    totalPrice += price * amount;
  }

  return totalPrice;
};

/**
 * Высчитывает итоговую стоимость заказа, учитывая двери, аксессуары,
 * удорожания/скидки и + сборку (через calculateAssembly).
 */
export const getTotalPriceWithDiscount = (
  selectedProducts,
  priceIncrease = 0,
  discount = 0
) => {
  if (!Array.isArray(selectedProducts) || selectedProducts.length === 0) {
    return 0;
  }

  let total = 0;

  for (let product of selectedProducts) {
    const basePrice =
      product.price || (product.product && product.product.price) || 0;
    const amount = product.amount || 1;
    let doorPrice = basePrice * amount;

    // 1) Спец. удорожание для алюминия цвета GOLD / ЗОЛОТО (+10%)
    const rawColor =
      product.aluminium ||
      (product.product && (product.product.aluminium || product.product.aluminiumColor)) ||
      "";
    const color = String(rawColor).toUpperCase();
    if (color === "GOLD" || color === "ЗОЛОТО") {
      doorPrice += Math.round(doorPrice * 0.10);
    }

    // 2) Применяем локальное удорожание двери
    const appreciation = product.appreciationPercentage || 0;
    if (appreciation > 0) {
      doorPrice += Math.round(doorPrice * (appreciation / 100));
    }

    // 3) Применяем локальную скидку на дверь
    const doorDiscount = product.orderDiscount || 0;
    if (doorDiscount > 0) {
      doorPrice -= Math.round(doorPrice * (doorDiscount / 100));
    }

    // 4) Глобальная скидка
    if (discount > 0) {
      doorPrice -= Math.round(doorPrice * (discount / 100));
    }

    // 5) Сборка (если есть)
    let assemblyPrice = 0;
    if (isDoor(product) && product.assembly) {
      const assemblyObj = calculateAssembly([product]);
      assemblyPrice = assemblyObj?.price || 0;
    }
    let doorPriceWithAssembly = doorPrice + assemblyPrice;

    // 6) Считаем сумму аксессуаров (каждый со своей скидкой/надбавкой)
    let accessoriesSum = 0;
    if (isDoor(product)) {
      if (product.doorKnob) {
        accessoriesSum += calcAccessoryFinalPrice(product.doorKnob, amount);
      }
      if (product.doorstep) {
        accessoriesSum += calcAccessoryFinalPrice(product.doorstep, amount);
      }
      if (product.doorPanel) {
        accessoriesSum += calcAccessoryFinalPrice(product.doorPanel, amount);
      }
      if (product.latch) {
        accessoriesSum += calcAccessoryFinalPrice(product.latch, amount);
      }
    }

    // 7) Дополнительные петли
    if (product.addPetli > 0) {
      const thickness =
        (product.product && product.product.thickness) || product.thickness || 0;
      const hingePrice = thickness === 57 ? 15000 : thickness === 42 ? 12000 : 0;
      accessoriesSum += hingePrice * product.addPetli * amount;
    }

    // 8) Складываем дверь + аксессуары
    let rowSubtotal = doorPriceWithAssembly + accessoriesSum;

    // 9) Применяем "глобальное удорожание" (priceIncrease)
    if (priceIncrease > 0) {
      rowSubtotal += Math.round(rowSubtotal * (priceIncrease / 100));
    }

    total += rowSubtotal;
  }

  return total;
};

/**
 * Простейший форматер чисел для отображения в таблице (с пробелами).
 */
export const currencyFormat = (num, minimumFractionDigits = 0) => {
  if (num == null || Number.isNaN(parseInt(num))) {
    return "0";
  }
  try {
    const formatter = new Intl.NumberFormat("en-US", {
      style: "decimal",
      minimumFractionDigits,
    });
    return formatter.format(num).replace(/,/g, " ");
  } catch (e) {
    return "0";
  }
};

/**
 * Пример отдельного хелпера для подсчёта цены c учётом discount/appreciation.
 * (необязательно, если не используете)
 */
export const getOrderPriceWithDiscountAndAppreciationPercentage = (
  productPrice,
  discount = 0,
  appreciationPercentage = 0
) => {
  if (!productPrice || isNaN(productPrice)) {
    return 0;
  }

  const appreciationValue = Math.round(
    productPrice * (appreciationPercentage / 100)
  );
  const priceWithAppreciationPercentage = productPrice + appreciationValue;
  const discountValue = Math.round(
    priceWithAppreciationPercentage * (discount / 100)
  );
  const resultWithDiscount = priceWithAppreciationPercentage - discountValue;
  return resultWithDiscount;
};
